// Reads the General settings section once on app boot and exposes the resolved
// values to consumers. Re-fetches when the cache is invalidated (settings save).

import { useQuery } from '@tanstack/react-query';
import { apiSingle } from '../lib/api.js';

export interface GeneralSettings {
  displayLanguage: string;
  dateDisplay: 'system' | 'short' | 'medium' | 'long';
  itemsPerPage: number;
  slimSidebar: boolean;
  darkMode: 'system' | 'light' | 'dark';
  invertThumbnails: boolean;
  // CTSK-283 — themeColor left this envelope: the accent color is a
  // per-org attribute now (organizations.theme_color, applied from
  // session.activeOrg.themeColor). The server-side envelope key is
  // untouched for backward compat but the web app no longer reads it.
  enableUpdateChecking: boolean;
  showDocumentCountsInSidebar: boolean;
  warnOnUnsavedChanges: boolean;
}

interface SettingsEnvelope {
  section: string;
  body: GeneralSettings;
}

const FALLBACK: GeneralSettings = {
  displayLanguage: 'system',
  dateDisplay: 'system',
  itemsPerPage: 25,
  slimSidebar: false,
  darkMode: 'system',
  invertThumbnails: false,
  enableUpdateChecking: false,
  showDocumentCountsInSidebar: true,
  warnOnUnsavedChanges: true,
};

export function useSettings(): GeneralSettings {
  const q = useQuery({
    queryKey: ['settings', 'general'],
    queryFn: () => apiSingle<SettingsEnvelope>('/settings/general'),
    staleTime: 30_000,
    retry: 0,
  });
  // Merge with fallback so consumers can rely on every field even before the
  // network round-trip completes.
  return { ...FALLBACK, ...(q.data?.body ?? {}) };
}
